if (!customElements.get('product-card-carousel')) {
  class ProductCardCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.slideCount = parseInt(this.dataset.slideCount) || 1;
      this._isTransitioning = false;
    }

    connectedCallback() {
      if (this.slideCount <= 1) return;

      this.track = this.querySelector('.product-card-carousel__track');
      this.prevBtn = this.querySelector('.product-card-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.product-card-carousel__arrow--next');

      // Clone first and last slides for seamless wrap
      this._setupInfiniteLoop();

      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._hydrateImages();
        if (!this._isTransitioning) this.goTo(this.currentSlide - 1);
      });
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._hydrateImages();
        if (!this._isTransitioning) this.goTo(this.currentSlide + 1);
      });

      // Touch/swipe — disabled on mobile when inside a swiper carousel
      // to prevent conflicts with the parent swiper's swipe gesture
      this._insideSwiper = !!this.closest('.swiper');

      this._swipedThisTouch = false;
      this.track.addEventListener('touchstart', (e) => {
        this._hydrateImages();
        this.onTouchStart(e);
      }, { passive: true });
      this.track.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
      this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });

      const parentLink = this.closest('a');
      if (parentLink) {
        parentLink.addEventListener('click', (e) => {
          if (this._swipedThisTouch) {
            e.preventDefault();
            this._swipedThisTouch = false;
          }
        });
      }

      this.querySelectorAll('img').forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Listen for transition end to snap after wrapping
      this.track.addEventListener('transitionend', () => this._onTransitionEnd());

      // Mobile swipe hint: on collection pages, the first carousel plays
      // a brief peek animation to indicate images are swipeable.
      if (!ProductCardCarousel._hintShown && window.innerWidth < 750) {
        var isCollection = !!document.getElementById('product-grid');
        if (isCollection) {
          ProductCardCarousel._hintShown = true;
          this._playSwipeHint();
        }
      }
    }

    _playSwipeHint() {
      var self = this;
      var track = this.track;
      if (!track) return;

      setTimeout(function() {
        // Current position is _getOffset(0) = -100%
        var peekPercent = -125; // shift left 25% to reveal next image edge
        track.style.transition = 'transform 0.4s ease-out';
        track.style.transform = 'translateX(' + peekPercent + '%)';

        setTimeout(function() {
          track.style.transition = 'transform 0.35s ease-in';
          track.style.transform = 'translateX(-100%)';

          // Clean up after animation completes
          setTimeout(function() {
            track.style.transition = 'none';
          }, 360);
        }, 500);
      }, 800);
    }

    _setupInfiniteLoop() {
      const slides = this.track.querySelectorAll('.product-card-carousel__slide');
      if (slides.length < 2) return;

      // Clone last slide → prepend before first
      const lastClone = slides[slides.length - 1].cloneNode(true);
      lastClone.classList.add('product-card-carousel__clone');
      lastClone.removeAttribute('data-slide-index');
      this._deferCloneImages(lastClone);
      this.track.insertBefore(lastClone, slides[0]);

      // Clone first slide → append after last
      const firstClone = slides[0].cloneNode(true);
      firstClone.classList.add('product-card-carousel__clone');
      firstClone.removeAttribute('data-slide-index');
      this._deferCloneImages(firstClone);
      this.track.appendChild(firstClone);

      // Position at real first slide (offset by 1 for the prepended clone)
      this._setPosition(this._getOffset(0), false);
    }

    // Strip src/srcset from a cloned slide's images so the browser does not
    // fetch or decode them until the user actually interacts with this
    // carousel. The clones only need to be visible during the brief
    // wrap-around animation when the user swipes past the last or first
    // slide; the original slides serve all the normal-state rendering.
    //
    // Why this matters: connectedCallback runs for every <product-card-
    // carousel> on the page. On a collection grid with 30+ multi-image
    // cards, the previous implementation cloned every first AND last slide,
    // adding 60+ extra <img> decodes on top of the originals. Combined with
    // load-more pagination piling more cards into the DOM, this routinely
    // exceeded iOS Safari's per-tab renderer memory ceiling and the OS
    // killed the renderer — producing the "A problem repeatedly occurred"
    // dialog on scroll. Deferring the clone decodes until interaction
    // eliminates the memory cost for the vast majority of carousels users
    // never touch.
    _deferCloneImages(slideEl) {
      slideEl.querySelectorAll('img').forEach(img => {
        if (img.getAttribute('src')) img.dataset.lazySrc = img.getAttribute('src');
        if (img.getAttribute('srcset')) img.dataset.lazySrcset = img.getAttribute('srcset');
        img.removeAttribute('src');
        img.removeAttribute('srcset');
      });
    }

    // Restore deferred images on first user interaction (touchstart or arrow
    // click) so the browser has time to fetch them before they animate into
    // view. Idempotent — safe to call repeatedly. Covers BOTH:
    //   - real secondary slides rendered with data-defer-src (card-product.liquid
    //     defers every slide after the first so untouched cards decode only one
    //     image), and
    //   - the wrap-around clones whose images were stripped to data-lazy-src by
    //     _deferCloneImages.
    // This is the JS half of the iOS memory-crash fix: image bytes for slides
    // the shopper never looks at are never fetched or decoded.
    _hydrateImages() {
      if (this._imagesHydrated) return;
      this._imagesHydrated = true;
      this.querySelectorAll('img[data-defer-src], img[data-lazy-src]').forEach(img => {
        const srcset = img.dataset.deferSrcset || img.dataset.lazySrcset;
        const src = img.dataset.deferSrc || img.dataset.lazySrc;
        if (srcset) img.setAttribute('srcset', srcset);
        if (src) img.setAttribute('src', src);
      });
    }

    _getOffset(index) {
      // index 0 = first real slide, which is at DOM position 1 (after prepended clone)
      return -((index + 1) * 100);
    }

    _setPosition(percent, animate) {
      if (animate) {
        this.track.style.transition = 'transform 0.3s ease';
      } else {
        this.track.style.transition = 'none';
      }
      this.track.style.transform = `translateX(${percent}%)`;
    }

    goTo(index) {
      this._isTransitioning = true;

      if (index >= this.slideCount) {
        // Moving past last → animate to clone-of-first (at DOM end)
        this.currentSlide = 0;
        this._wrapTarget = 0;
        this._setPosition(this._getOffset(this.slideCount), true);
      } else if (index < 0) {
        // Moving before first → animate to clone-of-last (at DOM start)
        this.currentSlide = this.slideCount - 1;
        this._wrapTarget = this.slideCount - 1;
        this._setPosition(this._getOffset(-1), true);
      } else {
        this.currentSlide = index;
        this._wrapTarget = null;
        this._setPosition(this._getOffset(index), true);
      }
    }

    _onTransitionEnd() {
      if (this._wrapTarget !== null && this._wrapTarget !== undefined) {
        // Snap instantly to the real slide (no animation)
        this._setPosition(this._getOffset(this._wrapTarget), false);
        this._wrapTarget = null;
      }
      this._isTransitioning = false;
    }

    onTouchStart(e) {
      // On mobile, skip if inside a swiper — let parent swiper handle swipe
      if (this._insideSwiper && window.innerWidth < 750) return;
      if (this._isTransitioning) return;
      this.isDragging = true;
      this._locked = false;
      this._swipedThisTouch = false;
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
      this.currentX = this.startX;
      this.track.style.transition = 'none';
    }

    onTouchMove(e) {
      if (!this.isDragging) return;
      this.currentX = e.touches[0].clientX;
      const diffX = this.currentX - this.startX;
      const diffY = e.touches[0].clientY - this.startY;

      if (!this._locked && Math.abs(diffY) > Math.abs(diffX)) {
        this.isDragging = false;
        return;
      }
      this._locked = true;
      e.preventDefault();

      const baseOffset = this._getOffset(this.currentSlide);
      const dragPercent = (diffX / this.offsetWidth) * 100;
      this.track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
    }

    onTouchEnd() {
      if (!this.isDragging) {
        this._locked = false;
        return;
      }
      this.isDragging = false;
      this._locked = false;

      const diffX = this.currentX - this.startX;
      const threshold = this.offsetWidth * 0.15;

      if (Math.abs(diffX) > 10) {
        this._swipedThisTouch = true;
      }

      if (diffX < -threshold) {
        this.goTo(this.currentSlide + 1);
      } else if (diffX > threshold) {
        this.goTo(this.currentSlide - 1);
      } else {
        // Snap back
        this._setPosition(this._getOffset(this.currentSlide), true);
        this._isTransitioning = true;
      }
    }
  }

  customElements.define('product-card-carousel', ProductCardCarousel);
}
